import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type ToastType = "success" | "error" | "info" | "warning";

interface ToastOptions {
  type?: ToastType;
  durationMs?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  durationMs: number;
  action?: { label: string; onClick: () => void };
}

class ToastManager {
  listeners = new Set<(items: ToastItem[]) => void>();
  items: ToastItem[] = [];

  show(message: string, opts: ToastOptions = {}) {
    const id = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random());
    const type = opts.type || "info";
    const durationMs = opts.durationMs ?? (type === "error" ? 5000 : 3000);
    this.items = [...this.items, { id, message, type, durationMs, action: opts.action }];
    this.notify();
    setTimeout(() => this.dismiss(id), durationMs);
  }

  dismiss(id: string) {
    this.items = this.items.filter((t) => t.id !== id);
    this.notify();
  }

  notify() {
    this.listeners.forEach((l) => l(this.items));
  }

  success(m: string, o: Omit<ToastOptions, "type"> = {}) {
    this.show(m, { ...o, type: "success" });
  }
  error(m: string, o: Omit<ToastOptions, "type"> = {}) {
    this.show(m, { ...o, type: "error" });
  }
  info(m: string, o: Omit<ToastOptions, "type"> = {}) {
    this.show(m, { ...o, type: "info" });
  }
  warning(m: string, o: Omit<ToastOptions, "type"> = {}) {
    this.show(m, { ...o, type: "warning" });
  }
}

export const toast = new ToastManager();

const iconMap: Record<ToastType, string> = {
  success: "check_circle",
  error: "error",
  warning: "warning",
  info: "info",
};

export function ToastContainer() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    const listener = (newItems: ToastItem[]) => setItems(newItems);
    toast.listeners.add(listener);
    return () => { toast.listeners.delete(listener); };
  }, []);

  if (items.length === 0) return null;

  return createPortal(
    <div className="toast-container">
      {items.map((item) => (
        <div key={item.id} className={`toast toast-${item.type}`}>
          <span className={`material-symbols-outlined toast-icon`}>{iconMap[item.type]}</span>
          <span className="toast-msg">{item.message}</span>
          {item.action && (
            <button className="toast-action" onClick={item.action.onClick}>
              {item.action.label}
            </button>
          )}
          <button className="toast-close" onClick={() => toast.dismiss(item.id)} aria-label="Đóng">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
}
